import React from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../i18n/LanguageContext';
import { PET_PRODUCTS } from '../data/petProducts';
import { getProductsForCategory } from '../data/productCatalog';
import siteImageManifest from '../data/siteImageManifest.generated.json';

const PET_CAPACITY_MIN = 25;
const PET_CAPACITY_MAX = 10000;

const PET_FILTERS = {
  neckTypes: ['pco1881', 'pco1810', 'oil2921', 'neck3025', 'neck38mm', 'neck48mm'],
  shapes: ['round', 'oval', 'square', 'rectangular'],
};

const PET_INDUSTRY_ORDER = [
  'water',
  'alcoholic',
  'beer',
  'church',
  'chemicals',
  'sportsdrink',
  'soap',
  'cosmetics',
  'detergent',
  'pharma',
  'fresh',
  'icetea',
  'dairy',
  'vinegar',
  'nonalcoholic',
  'syrups',
  'oil',
  'wine',
];

const PET_INDUSTRY_SET = new Set(PET_INDUSTRY_ORDER);

const INDUSTRY_ALIASES = {
  water: 'water',
  apa: 'water',
  alcoholic: 'alcoholic',
  'bauturi alcoolice': 'alcoholic',
  beer: 'beer',
  bere: 'beer',
  church: 'church',
  biserica: 'church',
  chemicals: 'chemicals',
  chimice: 'chemicals',
  sportsdrink: 'sportsdrink',
  'bauturi sport': 'sportsdrink',
  soap: 'soap',
  sapun: 'soap',
  cosmetics: 'cosmetics',
  cosmetice: 'cosmetics',
  detergent: 'detergent',
  pharma: 'pharma',
  farmaceutice: 'pharma',
  fresh: 'fresh',
  'fresh smoothies': 'fresh',
  'fresh & smoothies': 'fresh',
  icetea: 'icetea',
  'ice tea': 'icetea',
  'ice-tea': 'icetea',
  dairy: 'dairy',
  lactate: 'dairy',
  vinegar: 'vinegar',
  otet: 'vinegar',
  nonalcoholic: 'nonalcoholic',
  racoritoare: 'nonalcoholic',
  syrups: 'syrups',
  siropuri: 'syrups',
  oil: 'oil',
  ulei: 'oil',
  wine: 'wine',
  vin: 'wine',
};

const PET_GROUP_DEFAULT_ORDER = [
  'karolacta',
  'lapte-patrat',
  'lapte',
  'laptic',
  'paco',
  'basilica',
  'select',
  'elegant-lapte',
  'omnia',
  'vio',
  'baby',
  'cristal',
  'comuna',
  'comuna-cu-cruce',
  'standard',
  'standard-ulei',
  'otet',
  'fresh',
  'rotund',
  'tineret',
  'minipocket',
  'perfect',
  'plosca',
  'damigeana',
  'sapun',
  'finete',
  'elegant',
  'lebada',
  'crin',
  'practic',
  'balsam-nr-1',
  'balsam-nr-2',
  'flacon',
  'miniflacon',
  'picola',
  'minipicola',
  'mini',
  'avantaj',
  'premium',
  'jumbo',
];

const PET_GROUP_DEFAULT_ORDER_MAP = new Map(PET_GROUP_DEFAULT_ORDER.map((key, index) => [key, index]));


function toggleInList(list, value) {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

function clampNumber(num, min, max) {
  if (Number.isNaN(num)) return min;
  return Math.min(max, Math.max(min, num));
}

function hasAnyOverlap(a, b) {
  if (!b.length) return true; // no filter selected
  if (!a.length) return false; // product has no metadata for this filter
  const setB = new Set(b);
  return a.some((x) => setB.has(x));
}

function normalizeTextValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s*&\s*/g, ' & ')
    .replace(/[^a-z0-9&\s-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function matchesSearchText(candidate, query) {
  const normalizedCandidate = normalizeTextValue(candidate);
  const normalizedQuery = normalizeTextValue(query);
  if (!normalizedQuery) return true;
  return normalizedCandidate.includes(normalizedQuery);
}

function toIndustryId(value) {
  const normalized = normalizeTextValue(value);
  if (!normalized) return '';

  if (PET_INDUSTRY_SET.has(normalized)) return normalized;
  if (INDUSTRY_ALIASES[normalized]) return INDUSTRY_ALIASES[normalized];

  return '';
}

function getVariantIndustryIds(variant) {
  const fromIndustries = Array.isArray(variant?.industries) ? variant.industries.map(toIndustryId).filter(Boolean) : [];
  if (fromIndustries.length) return Array.from(new Set(fromIndustries));

  const fromSegments = Array.isArray(variant?.segments) ? variant.segments.map(toIndustryId).filter(Boolean) : [];
  return Array.from(new Set(fromSegments));
}

function matchesPetVariant(variant, { neckTypes, shapes, industries, capFrom, capTo }) {
  if (!hasAnyOverlap(variant.neckTypes, neckTypes)) return false;
  if (!hasAnyOverlap(variant.shapes, shapes)) return false;
  const variantIndustries = getVariantIndustryIds(variant);
  if (!hasAnyOverlap(variantIndustries, industries)) return false;
  if (variant.fillVolumeMl < capFrom || variant.fillVolumeMl > capTo) return false;
  return true;
}

function inferBaseNameFromVariantTitle(title) {
  if (!title) return '';
  return String(title)
    .replace(/^\s*[\d.,]+\s*ml\s+/i, '')
    .trim();
}

function toSentenceCaseName(name) {
  const s = String(name || '').trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function getGroupTitle(t, group) {
  const preferred = group.variants.find((v) => v.i18nKey) || group.variants[0] || null;
  let baseName = group.displayName || '';
  if (String(group?.groupKey || '').toLowerCase() === 'lapte-patrat') {
    baseName = 'Lapte Pătrat';
  } else if (String(group?.groupKey || '').toLowerCase() === 'fresh') {
    baseName = 'Smoothies';
  }

  if (!baseName && preferred && preferred.i18nKey) {
    const titleKey = `${preferred.i18nKey}.title`;
    const translated = t(titleKey);
    if (translated !== titleKey) baseName = inferBaseNameFromVariantTitle(translated);
  }

  if (!baseName) baseName = group.groupKey || 'PET';
  return `${toSentenceCaseName(baseName)} ${group.volumesLabel || ''}`.trim();
}

function getVariantTitle(t, group, variant) {
  const displayVolumeMl = (() => {
    const id = String(variant?.id || '');
    if (id === 'pet-baby-330') return 350;
    if (id === 'pet-laptic-400') return 450;
    if (id === 'pet-minipocket-150') return 200;
    if (id === 'pet-minipicola-150') return 50;
    return typeof variant?.fillVolumeMl === 'number' ? variant.fillVolumeMl : 0;
  })();

  const displayBaseOverride = (() => {
    const groupKey = String(group?.groupKey || '').toLowerCase();
    const id = String(variant?.id || '').toLowerCase();

    if (groupKey === 'balsam-nr-1') return 'Balsam Nr I';
    if (groupKey === 'balsam-nr-2') return 'Balsam Nr II';
    if (groupKey === 'elegant-lapte') return 'Elegant Lapte';
    if (groupKey === 'lapte-patrat') return 'Lapte Pătrat';
    if (groupKey === 'cristal') return 'Cristalin';
    if (groupKey === 'finete') return 'Finețe';
    if (groupKey === 'laptic') return 'Lăptic';
    if (groupKey === 'lebada') return 'Lebădă';
    if (groupKey === 'standard-ulei') return 'Standard Ulei';
    if (groupKey === 'sapun') return 'Săpun';
    if (groupKey === 'omnia') return 'Voios';
    if (groupKey === 'fresh') return 'Smoothies';
    if (id === 'pet-lapte-400') return 'Bucuros';
    return '';
  })();

  const i18nBase = (() => {
    if (!variant?.i18nKey) return '';
    const key = `${variant.i18nKey}.title`;
    const translated = t(key);
    if (translated === key) return '';
    return inferBaseNameFromVariantTitle(translated);
  })();

  const base = (() => {
    if (displayBaseOverride) return displayBaseOverride;

    if (group?.displayName) return group.displayName;
    if (i18nBase) return i18nBase;
    const preferred = group?.variants?.find((v) => v.i18nKey) || group?.variants?.[0] || null;
    if (preferred?.i18nKey) {
      const key = `${preferred.i18nKey}.title`;
      const translated = t(key);
      if (translated !== key) return inferBaseNameFromVariantTitle(translated);
    }
    return group?.groupKey || 'PET';
  })();

  const ml = displayVolumeMl;
  const liters = (() => {
    if (!ml) return '';
    const val = ml / 1000;
    if (Number.isInteger(val)) return String(val);
    return String(val).replace(/0+$/g, '').replace(/\.$/, '');
  })();
  const displayBase = displayBaseOverride ? base : toSentenceCaseName(base);
  return `${displayBase} ${liters ? `${liters}L` : ''}`.trim();
}

function getMiniVariantOrder(id) {
  return 99;
}

function formatCapTitleFromId(id, lang) {
  const raw = String(id || '')
    .replace(/^cap-/, '')
    .replace(/^dop-/, '')
    .replace(/\bdop\b/gi, '')
    .replace(/-+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!raw) return '';
  if (lang !== 'en') return raw;

  return raw
    .split(' ')
    .filter(Boolean)
    .map((w) => {
      const upper = w.toUpperCase();
      if (upper === 'PCO') return 'PCO';
      if (/^\d+$/.test(w) || /^\d+\/\d+$/.test(w)) return w;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

function getCatalog(t) {
  return [
    { slug: 'sticle-pet', ...t('products.items.petBottles') },
    { slug: 'capace-manere', ...t('products.items.capsHandles') },
    { slug: 'preforme', ...t('products.items.preforms') },
    { slug: 'galeti-bidoane', ...t('products.items.bucketsGallons') },
  ];
}

function normalizeCategory(category) {
  const c = String(category || '').trim();
  if (!c) return 'sticle-pet';

  if (c === 'capace' || c === 'manere' || c === 'triggere') return 'capace-manere';
  if (c === 'galeti' || c === 'bidoane-hdpe') return 'galeti-bidoane';

  return c;
}

function getOtherProductsImplicitRank(product) {
  const type = String(product?.type || '').toLowerCase();
  const volumeMl = typeof product?.volumeMl === 'number' ? product.volumeMl : null;

  if (type === 'galeata') return 0;
  if (type === 'caserola') return 1;
  if (type === 'flacon' && volumeMl === 750) return 2;
  if (type === 'canistra') return 3;
  if (type === 'bidon') return 4;
  return 99;
}

export default function ProductCategory() {
  const { t, lang } = useI18n();
  const { category } = useParams();
  const navigate = useNavigate();

  const normalizedCategory = normalizeCategory(category);

  const isPet = normalizedCategory === 'sticle-pet';
  const isCapaceManere = normalizedCategory === 'capace-manere';
  const isGaleti = normalizedCategory === 'galeti-bidoane';
  const isPreforms = normalizedCategory === 'preforme';

  const v = typeof siteImageManifest?.generatedAt === 'string' ? siteImageManifest.generatedAt : '';
  const suffix = v ? `?v=${encodeURIComponent(v)}` : '';
  const preformsImageSrc = `${process.env.PUBLIC_URL}/images/preforme/Preforme.jpg${suffix}`;

  const [petNeckTypes, setPetNeckTypes] = React.useState([]);
  const [petShapes, setPetShapes] = React.useState([]);
  const [petIndustries, setPetIndustries] = React.useState([]);
  const [petCapacityFrom, setPetCapacityFrom] = React.useState(PET_CAPACITY_MIN);
  const [petCapacityTo, setPetCapacityTo] = React.useState(PET_CAPACITY_MAX);
  const [petTileImageIndex, setPetTileImageIndex] = React.useState(() => ({}));
  const [nonPetTileImageIndex, setNonPetTileImageIndex] = React.useState(() => ({}));
  const [capNeckTypes, setCapNeckTypes] = React.useState([]);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [sortMode, setSortMode] = React.useState('implicit');
  const [searchInput, setSearchInput] = React.useState('');
  const [appliedSearch, setAppliedSearch] = React.useState('');
  const [searchSuggestionsOpen, setSearchSuggestionsOpen] = React.useState(false);

  const GAL_TYPES = ['caserola', 'galeata', 'bidon', 'flacon', 'canistra'];
  const GAL_COLORS = ['gri', 'alb', 'transparent'];
  const GAL_SHAPES = ['round', 'oval', 'square'];

  const [galTypes, setGalTypes] = React.useState([]);
  const [galVolumeFrom, setGalVolumeFrom] = React.useState(0);
  const [galVolumeTo, setGalVolumeTo] = React.useState(20000);
  const [galColors, setGalColors] = React.useState([]);
  const [galShapes, setGalShapes] = React.useState([]);

  const petIndustryOptions = React.useMemo(() => {
    return PET_INDUSTRY_ORDER;
  }, []);

  const catalog = getCatalog(t);
  const item = catalog.find((x) => x.slug === normalizedCategory);

  const contactParams = new URLSearchParams();
  contactParams.set('category', normalizedCategory);
  if (isPet) {
    if (petNeckTypes.length) contactParams.set('neckTypes', petNeckTypes.join(','));
    if (petShapes.length) contactParams.set('shapes', petShapes.join(','));
    if (petIndustries.length) contactParams.set('industries', petIndustries.join(','));
    if (petCapacityFrom !== PET_CAPACITY_MIN) contactParams.set('capFrom', String(petCapacityFrom));
    if (petCapacityTo !== PET_CAPACITY_MAX) contactParams.set('capTo', String(petCapacityTo));
  }
  const contactUrl = `/contact?${contactParams.toString()}`;

  function toggleFilters() {
    setFiltersOpen((v) => !v);
  }

  function onSelectCategory(e) {
    const next = String(e.target.value || '').trim();
    if (!next) return;
    navigate(`/produse/${next}`);
  }

  function onChangeCapFrom(next) {
    const val = clampNumber(next, PET_CAPACITY_MIN, PET_CAPACITY_MAX);
    setPetCapacityFrom(val);
    if (val > petCapacityTo) setPetCapacityTo(val);
  }

  function onChangeCapTo(next) {
    const val = clampNumber(next, PET_CAPACITY_MIN, PET_CAPACITY_MAX);
    setPetCapacityTo(val);
    if (val < petCapacityFrom) setPetCapacityFrom(val);
  }

  function getNonPetProductTitle(product) {
    if (product?.i18nKey) {
      const key = `${product.i18nKey}.title`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return product?.title || product?.id || '';
  }

  function onTogglePetIndustry(industryId) {
    const currentScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
    setPetIndustries((v) => toggleInList(v, industryId));

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        window.scrollTo(0, currentScrollY);
      });
    }
  }

  function applySearch(nextValue) {
    const value = String(nextValue || '').trim();
    setSearchInput(value);
    setAppliedSearch(value);
    setSearchSuggestionsOpen(false);
  }

  const visiblePetProductGroups = isPet
    ? PET_PRODUCTS
    : [];

  const visiblePetVariants = isPet
    ? (() => {
        const list = visiblePetProductGroups
          .flatMap((g) =>
            g.variants
              .filter((v) =>
                matchesPetVariant(v, {
                  neckTypes: petNeckTypes,
                  shapes: petShapes,
                  industries: petIndustries,
                  capFrom: petCapacityFrom,
                  capTo: petCapacityTo,
                })
              )
              .map((v) => ({ group: g, variant: v }))
          )
          .sort((a, b) => {
            const orderA = PET_GROUP_DEFAULT_ORDER_MAP.get(a.group.groupKey);
            const orderB = PET_GROUP_DEFAULT_ORDER_MAP.get(b.group.groupKey);
            const rankA = orderA === undefined ? Number.MAX_SAFE_INTEGER : orderA;
            const rankB = orderB === undefined ? Number.MAX_SAFE_INTEGER : orderB;
            if (rankA !== rankB) return rankA - rankB;

            if (a.group.groupKey === 'mini' && b.group.groupKey === 'mini') {
              const miniA = getMiniVariantOrder(a.variant.id);
              const miniB = getMiniVariantOrder(b.variant.id);
              if (miniA !== miniB) return miniA - miniB;
            }

            const volA = typeof a.variant.fillVolumeMl === 'number' ? a.variant.fillVolumeMl : Number.MAX_SAFE_INTEGER;
            const volB = typeof b.variant.fillVolumeMl === 'number' ? b.variant.fillVolumeMl : Number.MAX_SAFE_INTEGER;
            if (volA !== volB) return volA - volB;

            return String(a.variant.id).localeCompare(String(b.variant.id));
          });

        if (sortMode === 'implicit') return list;
        const direction = sortMode === 'az' ? 1 : -1;
        return [...list].sort((a, b) => {
          const titleA = getVariantTitle(t, a.group, a.variant);
          const titleB = getVariantTitle(t, b.group, b.variant);
          return direction * titleA.localeCompare(titleB, lang, { sensitivity: 'base' });
        });
      })()
    : [];

  const petSearchItems = React.useMemo(
    () =>
      visiblePetVariants.map(({ group, variant }) => ({
        id: variant.id,
        title: getVariantTitle(t, group, variant),
      })),
    [t, visiblePetVariants]
  );

  const nonPetProducts = !isPet ? getProductsForCategory(category) : [];
  const nonPetBaseOrderMap = new Map();
  if (Array.isArray(nonPetProducts)) {
    nonPetProducts.forEach((product, index) => {
      nonPetBaseOrderMap.set(product.id, index);
    });
  }

  const visibleNonPetProducts = (() => {
    let list = Array.isArray(nonPetProducts) ? nonPetProducts.slice() : [];

    if (isCapaceManere) {
      if (capNeckTypes.length) {
        const set = new Set(capNeckTypes);
        list = list.filter((p) => Array.isArray(p.neckTypes) && p.neckTypes.some((n) => set.has(n)));
      }
    }

    if (isGaleti) {
      if (galTypes.length) {
        const set = new Set(galTypes);
        list = list.filter((p) => p.type && set.has(p.type));
      }
      list = list.filter((p) => {
        if (typeof p.volumeMl !== 'number') return true;
        return p.volumeMl >= galVolumeFrom && p.volumeMl <= galVolumeTo;
      });
      if (galShapes.length) {
        const set = new Set(galShapes);
        list = list.filter((p) => p.shape && set.has(p.shape));
      }
      if (galColors.length) {
        const set = new Set(galColors);
        list = list.filter((p) => Array.isArray(p.colors) && p.colors.some((c) => set.has(c)));
      }
    }

    if (sortMode === 'implicit') {
      if (!isGaleti) return list;

      return [...list].sort((a, b) => {
        const rankA = getOtherProductsImplicitRank(a);
        const rankB = getOtherProductsImplicitRank(b);
        if (rankA !== rankB) return rankA - rankB;

        const idxA = nonPetBaseOrderMap.has(a.id) ? nonPetBaseOrderMap.get(a.id) : Number.MAX_SAFE_INTEGER;
        const idxB = nonPetBaseOrderMap.has(b.id) ? nonPetBaseOrderMap.get(b.id) : Number.MAX_SAFE_INTEGER;
        return idxA - idxB;
      });
    }

    const direction = sortMode === 'az' ? 1 : -1;
    return [...list].sort((a, b) => {
      const titleA = getNonPetProductTitle(a);
      const titleB = getNonPetProductTitle(b);
      return direction * titleA.localeCompare(titleB, lang, { sensitivity: 'base' });
    });
  })();

  const nonPetSearchItems = React.useMemo(
    () =>
      visibleNonPetProducts.map((product) => ({
        id: product.id,
        title: getNonPetProductTitle(product),
      })),
    [visibleNonPetProducts]
  );

  const searchItems = isPet ? petSearchItems : nonPetSearchItems;

  const searchSuggestions = React.useMemo(() => {
    const query = String(searchInput || '').trim();
    if (!query) return [];

    const deduped = [];
    const seen = new Set();
    for (const item of searchItems) {
      if (!matchesSearchText(item.title, query)) continue;
      const key = normalizeTextValue(item.title);
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(item);
      if (deduped.length >= 8) break;
    }
    return deduped;
  }, [searchInput, searchItems]);

  const filteredPetVariants = React.useMemo(() => {
    if (!isPet) return [];
    const query = String(appliedSearch || '').trim();
    if (!query) return visiblePetVariants;
    return visiblePetVariants.filter(({ group, variant }) => matchesSearchText(getVariantTitle(t, group, variant), query));
  }, [appliedSearch, isPet, t, visiblePetVariants]);

  const filteredNonPetProducts = React.useMemo(() => {
    if (isPet) return [];
    const query = String(appliedSearch || '').trim();
    if (!query) return visibleNonPetProducts;
    return visibleNonPetProducts.filter((product) => matchesSearchText(getNonPetProductTitle(product), query));
  }, [appliedSearch, isPet, visibleNonPetProducts]);

  const hasActiveSearch = Boolean(String(appliedSearch || '').trim());
  const hasSearchResults = isPet ? filteredPetVariants.length > 0 : filteredNonPetProducts.length > 0;

  if (normalizedCategory !== category) {
    return <Navigate to={`/produse/${normalizedCategory}`} replace />;
  }

  if (!item) return <Navigate to="/produse/sticle-pet" replace />;

  return (
    <section className="yk-page">
      <div className="yk-catHeader">
        <div className="yk-catTitle">{t('products.categoryBarTitle')}</div>
        <div className="yk-catDropdown">
          <select
            className="yk-input yk-catSelect"
            value={normalizedCategory}
            onChange={onSelectCategory}
            aria-label={t('products.categoryBarTitle')}
          >
            {catalog.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
        <div className="yk-catBar" role="navigation" aria-label={t('nav.products')}>
          {catalog.map((c) => (
            <Link
              key={c.slug}
              className={c.slug === category ? 'yk-catItem is-active' : 'yk-catItem'}
              to={`/produse/${c.slug}`}
            >
              {c.title}
            </Link>
          ))}
        </div>
      </div>

      <div className="yk-filterToggleWrap">
        <button className="yk-filterToggle" type="button" aria-expanded={filtersOpen} onClick={toggleFilters}>
          {t('products.filters.title')}
        </button>
      </div>

      <div className={isPet || isCapaceManere || isGaleti ? 'yk-productLayout' : undefined}>
        {(isPet || isCapaceManere || isGaleti) ? (
          <aside className={`yk-filtersSidebar ${filtersOpen ? 'is-open' : 'is-collapsed'}`}>
            <div className="yk-card yk-sortCard">
              <div className="yk-searchBarWrap">
                <form
                  className="yk-searchBar"
                  onSubmit={(e) => {
                    e.preventDefault();
                    applySearch(searchInput);
                  }}
                >
                  <input
                    className="yk-input yk-searchInput"
                    type="search"
                    value={searchInput}
                    placeholder={t('products.search.placeholder')}
                    aria-label={t('products.search.placeholder')}
                    onChange={(e) => {
                      const nextValue = String(e.target.value || '');
                      setSearchInput(nextValue);
                      setSearchSuggestionsOpen(Boolean(nextValue.trim()));
                      if (!nextValue.trim()) setAppliedSearch('');
                    }}
                    onFocus={() => setSearchSuggestionsOpen(Boolean(String(searchInput || '').trim()))}
                    onBlur={() => {
                      window.setTimeout(() => setSearchSuggestionsOpen(false), 120);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setSearchSuggestionsOpen(false);
                      }
                    }}
                  />

                  {searchSuggestionsOpen && searchSuggestions.length ? (
                    <div className="yk-searchSuggestions" role="listbox" aria-label={t('products.search.suggestions')}>
                      {searchSuggestions.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className="yk-searchSuggestion"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applySearch(item.title)}
                        >
                          {item.title}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </form>
              </div>

              <label className="yk-label yk-sortLabel yk-sortLabelCompact">
                <span className="yk-filterGroupTitle">{t('products.filters.sort')}</span>
                <select
                  className="yk-input yk-sortSelect"
                  value={sortMode}
                  onChange={(e) => setSortMode(String(e.target.value || 'implicit'))}
                  aria-label={t('products.filters.sort')}
                >
                  <option value="implicit">{t('products.filters.options.sort.implicit')}</option>
                  <option value="az">{t('products.filters.options.sort.az')}</option>
                  <option value="za">{t('products.filters.options.sort.za')}</option>
                </select>
              </label>
            </div>

            <div className="yk-card yk-filters">
              <h2 className="yk-h2">{t('products.filters.title')}</h2>

              {isPet ? (
                <>
                  <div className="yk-filterGroup">
                    <div className="yk-filterGroupTitle">{t('products.filters.neckType')}</div>
                    <div className="yk-checkList">
                      {PET_FILTERS.neckTypes.map((id) => (
                        <label key={id} className="yk-check">
                          <input
                            type="checkbox"
                            checked={petNeckTypes.includes(id)}
                            onChange={() => setPetNeckTypes((v) => toggleInList(v, id))}
                          />
                          <span>{t(`products.filters.options.neckTypes.${id}`)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="yk-filterGroup">
                    <div className="yk-filterGroupTitle">{t('products.filters.capacity')}</div>

                    <div className="yk-rangeRow">
                      <input
                        className="yk-range"
                        type="range"
                        min={PET_CAPACITY_MIN}
                        max={PET_CAPACITY_MAX}
                        step={10}
                        value={petCapacityFrom}
                        onChange={(e) => onChangeCapFrom(Number(e.target.value))}
                      />
                      <input
                        className="yk-range"
                        type="range"
                        min={PET_CAPACITY_MIN}
                        max={PET_CAPACITY_MAX}
                        step={10}
                        value={petCapacityTo}
                        onChange={(e) => onChangeCapTo(Number(e.target.value))}
                      />
                    </div>

                    <div className="yk-rangeInputs">
                      <label className="yk-label yk-labelInline">
                        {t('products.filters.from')}
                        <input
                          className="yk-input"
                          type="number"
                          min={PET_CAPACITY_MIN}
                          max={PET_CAPACITY_MAX}
                          value={petCapacityFrom}
                          onChange={(e) => onChangeCapFrom(Number(e.target.value))}
                        />
                      </label>
                      <label className="yk-label yk-labelInline">
                        {t('products.filters.to')}
                        <input
                          className="yk-input"
                          type="number"
                          min={PET_CAPACITY_MIN}
                          max={PET_CAPACITY_MAX}
                          value={petCapacityTo}
                          onChange={(e) => onChangeCapTo(Number(e.target.value))}
                        />
                      </label>
                    </div>

                    <p className="yk-help">{t('products.filters.capacityHint')}</p>
                  </div>

                  <div className="yk-filterGroup">
                    <div className="yk-filterGroupTitle">{t('products.filters.shape')}</div>
                    <div className="yk-checkList">
                      {PET_FILTERS.shapes.map((id) => (
                        <label key={id} className="yk-check">
                          <input
                            type="checkbox"
                            checked={petShapes.includes(id)}
                            onChange={() => setPetShapes((v) => toggleInList(v, id))}
                          />
                          <span>{t(`products.filters.options.shapes.${id}`)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {petIndustryOptions.length ? (
                    <div className="yk-filterGroup">
                      <div className="yk-filterGroupTitle">{t('products.filters.industry')}</div>
                      <div className="yk-checkList">
                        {petIndustryOptions.map((id) => (
                          <label key={id} className="yk-check">
                            <input
                              type="checkbox"
                              checked={petIndustries.includes(id)}
                              onChange={() => onTogglePetIndustry(id)}
                            />
                            <span>{t(`products.filters.options.segments.${id}`)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}

              {isCapaceManere ? (
                <>
                  <div className="yk-filterGroup">
                    <div className="yk-filterGroupTitle">{t('products.filters.neckType')}</div>
                    <div className="yk-checkList">
                      {PET_FILTERS.neckTypes.map((id) => (
                        <label key={id} className="yk-check">
                          <input
                            type="checkbox"
                            checked={capNeckTypes.includes(id)}
                            onChange={() => setCapNeckTypes((v) => toggleInList(v, id))}
                          />
                          <span>{t(`products.filters.options.neckTypes.${id}`)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}

              {isGaleti ? (
                <>
                  <div className="yk-filterGroup">
                    <div className="yk-filterGroupTitle">Tip</div>
                    <div className="yk-checkList">
                      {GAL_TYPES.map((id) => (
                        <label key={id} className="yk-check">
                          <input type="checkbox" checked={galTypes.includes(id)} onChange={() => setGalTypes((v) => toggleInList(v, id))} />
                          <span>{id.charAt(0).toUpperCase() + id.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="yk-filterGroup">
                    <div className="yk-filterGroupTitle">Volum (ml)</div>
                    <div className="yk-rangeRow">
                      <input
                        className="yk-range"
                        type="range"
                        min={0}
                        max={20000}
                        step={10}
                        value={galVolumeFrom}
                        onChange={(e) => setGalVolumeFrom(Number(e.target.value) || 0)}
                      />
                      <input
                        className="yk-range"
                        type="range"
                        min={0}
                        max={20000}
                        step={10}
                        value={galVolumeTo}
                        onChange={(e) => setGalVolumeTo(Number(e.target.value) || 0)}
                      />
                    </div>

                    <div className="yk-rangeInputs">
                      <label className="yk-label yk-labelInline">
                        De la
                        <input className="yk-input" type="number" value={galVolumeFrom} onChange={(e) => setGalVolumeFrom(Number(e.target.value) || 0)} />
                      </label>
                      <label className="yk-label yk-labelInline">
                        Până la
                        <input className="yk-input" type="number" value={galVolumeTo} onChange={(e) => setGalVolumeTo(Number(e.target.value) || 0)} />
                      </label>
                    </div>
                  </div>

                  <div className="yk-filterGroup">
                    <div className="yk-filterGroupTitle">Culoare</div>
                    <div className="yk-checkList">
                      {GAL_COLORS.map((c) => (
                        <label key={c} className="yk-check">
                          <input type="checkbox" checked={galColors.includes(c)} onChange={() => setGalColors((v) => toggleInList(v, c))} />
                          <span>{c.charAt(0).toUpperCase() + c.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="yk-filterGroup">
                    <div className="yk-filterGroupTitle">Formă</div>
                    <div className="yk-checkList">
                      {GAL_SHAPES.map((s) => (
                        <label key={s} className="yk-check">
                          <input type="checkbox" checked={galShapes.includes(s)} onChange={() => setGalShapes((v) => toggleInList(v, s))} />
                          <span>{s === 'round' ? 'Rotundă' : 'Pătrată'}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}

              <div className="yk-ctaRow">
                <Link className="yk-btn yk-btnPrimary" to={contactUrl}>
                  {t('products.filters.ctaButton')}
                </Link>
              </div>
            </div>
          </aside>
        ) : null}

        <div className="yk-productMain">
          <div className="yk-productHero is-centered">
            <h1 className="yk-h1">{item.title}</h1>
            <p className="yk-lead">{item.desc}</p>
          </div>

          {isPreforms ? (
            <div className="yk-twoCol" style={{ marginBottom: 18 }}>
              <div className="yk-card">
                <h2 className="yk-h2">{t('products.preformsInfoTitle')}</h2>
                <p className="yk-text">{t('products.preformsInfoBody')}</p>
              </div>

              <div className="yk-card yk-mediaCard" aria-hidden="true">
                <div className="yk-media16x9 yk-preformsMedia">
                  <img className="yk-heroImage" src={preformsImageSrc} alt="" loading="lazy" />
                </div>
              </div>
            </div>
          ) : null}

          <div className="yk-card">
            <h2 className="yk-h2">{t('products.detail.ctaTitle')}</h2>
            <p className="yk-text">{t('products.detail.ctaBody')}</p>
            <div className="yk-ctaRow">
              <Link className="yk-btn yk-btnPrimary" to={contactUrl}>
                {t('products.detail.ctaButton')}
              </Link>
            </div>
          </div>

          {isPet ? (
            <div className="yk-capNotice" role="note" aria-label="Disclaimer culori">
              <span className="yk-capNoticeIcon yk-capNoticeIconOk" aria-hidden="true">
                ✓
              </span>
              <div className="yk-capNoticeText">{t('products.pet.listDisclaimer')}</div>
            </div>
          ) : null}

          {isPet ? (
            <div className="yk-productGrid">
              {filteredPetVariants.map(({ group: g, variant }) => {
                const hasPhotos = Array.isArray(variant.images) && variant.images.length;
                const currentIndex = hasPhotos ? (petTileImageIndex[variant.id] || 0) : 0;
                const safeIndex = hasPhotos ? Math.min(currentIndex, variant.images.length - 1) : 0;
                const imgSrc = hasPhotos ? variant.images[safeIndex] : `${process.env.PUBLIC_URL}/brand/yk-mark.svg`;
                const isPhoto = Boolean(hasPhotos);

                const canCycle = hasPhotos && variant.images.length > 1;
                const prevLabel = t('common.prev') === 'common.prev' ? 'Previous photo' : t('common.prev');
                const nextLabel = t('common.next') === 'common.next' ? 'Next photo' : t('common.next');

                function showPrev() {
                  if (!canCycle) return;
                  setPetTileImageIndex((m) => {
                    const cur = m[variant.id] || 0;
                    const next = (cur - 1 + variant.images.length) % variant.images.length;
                    return { ...m, [variant.id]: next };
                  });
                }

                function showNext() {
                  if (!canCycle) return;
                  setPetTileImageIndex((m) => {
                    const cur = m[variant.id] || 0;
                    const next = (cur + 1) % variant.images.length;
                    return { ...m, [variant.id]: next };
                  });
                }

                return (
                  <div key={variant.id} className="yk-productTileWrap">
                    {canCycle ? (
                      <div className="yk-productTileArrows" aria-label="Photo navigation">
                        <button type="button" className="yk-productTileArrow is-left" aria-label={prevLabel} onClick={showPrev}>
                          ‹
                        </button>
                        <button type="button" className="yk-productTileArrow is-right" aria-label={nextLabel} onClick={showNext}>
                          ›
                        </button>
                      </div>
                    ) : null}

                    <Link className="yk-productTile" to={`/produse/sticle-pet/${variant.id}`}>
                      <div className="yk-productImgWrap">
                        <img
                          className={isPhoto ? 'yk-productImg is-photo' : 'yk-productImg'}
                          src={imgSrc}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <div className="yk-productTileBody">
                        <div className="yk-productTileTitle">{getVariantTitle(t, g, variant)}</div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
            ) : filteredNonPetProducts.length ? (
              <div className="yk-productGrid">
                {filteredNonPetProducts.map((p) => {
                  const photos = Array.isArray(p.images) ? p.images : [];
                  const hasPhotos = photos.length > 0;
                  const currentIndex = hasPhotos ? (nonPetTileImageIndex[p.id] || 0) : 0;
                  const safeIndex = hasPhotos ? Math.min(currentIndex, photos.length - 1) : 0;
                  const selectedImage = hasPhotos ? photos[safeIndex] : '';
                  const isPhoto = Boolean(selectedImage);
                  const imgSrc = selectedImage
                    ? `${process.env.PUBLIC_URL}${selectedImage}${suffix}`
                    : `${process.env.PUBLIC_URL}/brand/yk-mark.svg`;

                  const canCycle = hasPhotos && photos.length > 1;
                  const prevLabel = t('common.prev') === 'common.prev' ? 'Previous photo' : t('common.prev');
                  const nextLabel = t('common.next') === 'common.next' ? 'Next photo' : t('common.next');

                  function showPrev() {
                    if (!canCycle) return;
                    setNonPetTileImageIndex((m) => {
                      const cur = m[p.id] || 0;
                      const next = (cur - 1 + photos.length) % photos.length;
                      return { ...m, [p.id]: next };
                    });
                  }

                  function showNext() {
                    if (!canCycle) return;
                    setNonPetTileImageIndex((m) => {
                      const cur = m[p.id] || 0;
                      const next = (cur + 1) % photos.length;
                      return { ...m, [p.id]: next };
                    });
                  }

                  return (
                    <div key={p.id} className="yk-productTileWrap">
                      {canCycle ? (
                        <div className="yk-productTileArrows" aria-label="Photo navigation">
                          <button type="button" className="yk-productTileArrow is-left" aria-label={prevLabel} onClick={showPrev}>
                            ‹
                          </button>
                          <button type="button" className="yk-productTileArrow is-right" aria-label={nextLabel} onClick={showNext}>
                            ›
                          </button>
                        </div>
                      ) : null}

                      <Link className="yk-productTile" to={`/produse/${normalizedCategory}/${p.id}`}>
                        <div className="yk-productImgWrap">
                          <img
                            className={isPhoto ? 'yk-productImg is-photo' : 'yk-productImg'}
                            src={imgSrc}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        <div className="yk-productTileBody">
                          <div className="yk-productTileTitle">
                            {(() => {
                              const key = `${p.i18nKey}.title`;
                              const translated = t(key);
                              if (translated !== key) return translated;

                              if (p.i18nKey?.startsWith('products.caps.')) {
                                const suffix = p.i18nKey.slice('products.caps.'.length);
                                const altKey = `products.pet.caps.${suffix}.title`;
                                const alt = t(altKey);
                                if (alt !== altKey) return alt;
                              }

                              if (normalizedCategory === 'capace-manere') {
                                return formatCapTitleFromId(p.id, lang) || p.title || p.id;
                              }

                              return p.title || p.id;
                            })()}
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            ) : hasActiveSearch && !hasSearchResults ? (
              <div className="yk-card" style={{ marginTop: 18 }}>
                <h2 className="yk-h2">{t('products.search.noResultsTitle')}</h2>
                <p className="yk-text">{t('products.search.noResultsBody')}</p>
              </div>
            ) : isPreforms ? null : (
              <div className="yk-card" style={{ marginTop: 18 }}>
                <h2 className="yk-h2">{t('products.catalog.comingSoonTitle')}</h2>
                <p className="yk-text">{t('products.catalog.comingSoonBody')}</p>
              </div>
            )}
        </div>
      </div>
    </section>
  );
}
